import { useState, useCallback, Fragment, memo } from 'react';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import SyntheticEvent from 'UIComponents/core/SyntheticEvent';
import UIExpandableText from 'UIComponents/text/UIExpandableText';
import UIExpandingTextArea from 'UIComponents/input/UIExpandingTextArea';
import { getTextContentFromHtml } from 'sanitize-text/sanitizers/TextSanitizer';
import unescapedText from 'I18n/utils/unescapedText';
import { propertyLabelTranslator } from 'property-translator/propertyTranslator';
import emptyFunction from 'react-utils/emptyFunction';
import { getFullUrl } from 'hubspot-url-utils';
import PortalIdParser from 'PortalIdParser';
import once from 'transmute/once';
import toJS from 'transmute/toJS';
import { normalizeTypeId } from 'customer-data-objects/constants/ObjectTypeIds';
// @ts-expect-error Untyped dependency
import UIIFrame from 'ui-addon-iframeable/host/UIIFrame';
// @ts-expect-error Untyped dependency
import { MSG_TYPE_MODAL_DIALOG_CLOSE } from 'ui-addon-iframeable/messaging/IFrameMessageTypes';
import ModalIFrameLayer from './html/ModalIFrameLayer';
import { shouldShowPopovers } from '../v2/utils/shouldShowPopovers';
import { useObjectId } from '../v2/hooks/useObjectId';

// Returns true if the input should render as an embedded RTE.
//
// Currently this is used in the record creator and bulk edit modal: forms that
// use non-floating form controls, with new and/or unknown records (where there
// is no pre-existing content users want an at-a-glance preview of)
//
// - `use="floating"` cannot fit the embedded RTE (UIFloatingForm limits
//   the height of inputs to 32px; see HubSpot/PropertyCreateModal#1032)
// - `use="inline"` (inline edit in tables) and `use="compact"` (single-line
//   inputs in timeline cards) styles cannot fit the embedded RTE
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const shouldShowEmbedded = ({
  formControlUse,
  objectId,
  enablePopovers
}) => formControlUse === 'default' && !shouldShowPopovers({
  enablePopovers,
  objectId
});
const getEmbeddedIFrameUrl = (objectTypeId, propertyName) => `${getFullUrl('app')}/customer-data-properties-ui/${PortalIdParser.get()}/${encodeURIComponent(objectTypeId)}/${encodeURIComponent(propertyName)}/inline`;
const getModalIFrameUrl = (objectTypeId, propertyName) => `${getFullUrl('app')}/customer-data-properties-ui/${PortalIdParser.get()}/${encodeURIComponent(objectTypeId)}/${encodeURIComponent(propertyName)}/layer`;
const defaultIframePassthruProps = {
  id: 'property-input-html',
  // WARNING: some non-crm ATs rely on this (i.e. SignalsExtension)
  'data-test-id': 'property-input-html'
};

// Shown in the edit modal when the input is read-only
const readOnlyActionButtons = once(() => [{
  name: MSG_TYPE_MODAL_DIALOG_CLOSE,
  label: unescapedText('customerDataProperties.PropertyInputHtml.modal.actionButtons.close'),
  props: {
    use: 'tertiary-light'
  }
}]);

// Shown in the edit modal when the input is disabled
const disabledActionButtons = once(() => [{
  name: 'UPDATE',
  label: unescapedText('customerDataProperties.PropertyInputHtml.modal.actionButtons.update'),
  props: {
    use: 'primary',
    disabled: true
  }
}, {
  name: MSG_TYPE_MODAL_DIALOG_CLOSE,
  label: unescapedText('customerDataProperties.PropertyInputHtml.modal.actionButtons.cancel'),
  props: {
    use: 'secondary'
  }
}]);

// Shown in the edit modal when the input is editable
const editableActionButtons = once(() => [{
  name: 'UPDATE',
  label: unescapedText('customerDataProperties.PropertyInputHtml.modal.actionButtons.update'),
  props: {
    use: 'primary'
  }
}, {
  name: MSG_TYPE_MODAL_DIALOG_CLOSE,
  label: unescapedText('customerDataProperties.PropertyInputHtml.modal.actionButtons.cancel'),
  props: {
    use: 'secondary'
  }
}]);
const PropertyInputHtmlModal = ({
  property,
  objectType,
  className,
  autoFocus,
  disabled = false,
  readOnly = false,
  value,
  onChange,
  inputRef
}) => {
  const [initialized, setInitialized] = useState(false);
  const [show, setShow] = useState(false);
  const handleFocus = useCallback(() => {
    setInitialized(true);
    setShow(true);
  }, []);
  const handleClose = useCallback(({
    value: nextValue
  } = {}) => {
    if (typeof nextValue !== 'undefined') {
      onChange(SyntheticEvent(nextValue));
    }
    setShow(false);
  }, [onChange]);

  // unable to initialize the iframe (onInitError), or unable to establish
  // communication with the iframe within a timeout (onReadyError).
  // close the panel so the user isn't stuck looking at an empty panel.
  const handleIFrameError = useCallback(() => {
    FloatingAlertStore.addAlert({
      message: unescapedText('customerDataProperties.PropertyInputHtml.modal.error'),
      type: 'warning'
    });
    setInitialized(false);
    setShow(false);
  }, []);
  const propertyLabel = property.hubspotDefined ? propertyLabelTranslator(property.label) : property.label;
  let title;
  if (disabled || readOnly) {
    title = unescapedText('customerDataProperties.PropertyInputHtml.modal.title.view', {
      propertyLabel
    });
  } else {
    title = unescapedText('customerDataProperties.PropertyInputHtml.modal.title.edit', {
      propertyLabel
    });
  }
  let actionButtons;
  if (readOnly) {
    actionButtons = readOnlyActionButtons();
  } else if (disabled) {
    actionButtons = disabledActionButtons();
  } else {
    actionButtons = editableActionButtons();
  }
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(UIExpandableText, {
      buttonAlign: "left",
      hideButtonCaret: true,
      children: /*#__PURE__*/_jsx(UIExpandingTextArea, {
        className: className,
        autoFocus: autoFocus,
        disabled: disabled,
        readOnly: readOnly
        // `UIExpandingTextArea` input is for display only; all editing is done in the modal.
        // Make `onChange` a no-op instead of setting `readOnly={true}` so that the input
        // looks like it has editable styling, but clicking it only brings up the modal.
        ,
        onChange: emptyFunction,
        value: getTextContentFromHtml(value),
        onFocus: handleFocus,
        inputRef: inputRef,
        shrink: true
      })
    }), initialized && /*#__PURE__*/_jsx(ModalIFrameLayer, {
      actionButtons: actionButtons,
      embeddedPassthruProps: {
        property: toJS(property),
        defaultValue: value,
        autoFocus,
        readOnly,
        disabled
      },
      height: 500,
      iframePassthruProps: defaultIframePassthruProps,
      onClose: handleClose,
      onInitError: handleIFrameError,
      onMessage: emptyFunction,
      onReady: emptyFunction,
      onReadyError: handleIFrameError,
      show: show,
      showProgressIndicator: true,
      src: getModalIFrameUrl(normalizeTypeId(objectType), property.name),
      title: title,
      width: 800
    })]
  });
};
PropertyInputHtmlModal.displayName = 'PropertyInputHtmlModal';
const PropertyInputHtmlEmbedded = ({
  property,
  objectType,
  className,
  autoFocus,
  disabled = false,
  readOnly = false,
  value,
  onChange
}) => {
  // unable to initialize the iframe (onInitError), or unable to establish
  // communication with the iframe within a timeout (onReadyError).
  const handleIFrameError = useCallback(() => {
    FloatingAlertStore.addAlert({
      message: unescapedText('customerDataProperties.PropertyInputHtml.modal.error'),
      type: 'warning'
    });
  }, []);
  const handleMessage = useCallback(msg => {
    const {
      payload
    } = msg;
    if (payload && payload.type === 'CHANGE') {
      if (typeof payload.value !== 'undefined') {
        onChange(SyntheticEvent(payload.value));
      }
    }
  }, [onChange]);
  return /*#__PURE__*/_jsx(UIIFrame, {
    className: className,
    embeddedPassthruProps: {
      property: toJS(property),
      defaultValue: value,
      autoFocus,
      readOnly,
      disabled
    }
    // NOTE: 400px looks extremely tall for an embedded editor. But please remember:
    // the iframe needs to be tall enough to vertically accommodate any
    // plugins or popovers that the RTE might show (the emoji picker popover
    // and the insert link popover, for instance). These popovers cannot break the
    // bounds of the iframe, and will get clipped if insufficient vertical space is
    // available
    ,
    height: 400,
    iframePassthruProps: defaultIframePassthruProps,
    onInitError: handleIFrameError,
    onMessage: handleMessage,
    onReady: emptyFunction,
    onReadyError: handleIFrameError,
    showProgressIndicator: true,
    src: getEmbeddedIFrameUrl(normalizeTypeId(objectType), property.name),
    width: "100%"
  });
};
PropertyInputHtmlEmbedded.displayName = 'PropertyInputHtmlEmbedded';
const PropertyInputHtml = props => {
  const {
    formControlUse,
    enablePopovers
  } = props;
  const objectId = useObjectId();
  const InputComponent = shouldShowEmbedded({
    formControlUse,
    objectId,
    enablePopovers
  }) ? PropertyInputHtmlEmbedded : PropertyInputHtmlModal;
  return /*#__PURE__*/_jsx(InputComponent, Object.assign({}, props));
};
PropertyInputHtml.displayName = 'PropertyInputHtml';
export default /*#__PURE__*/memo(PropertyInputHtml);